import styled from 'styled-components';
import { formatVocalRange, voicePartForRange, voicePartName } from '../../../../common/src/learn_rules.ts';
import { LearnProgress } from '../../../../common/src/model.ts';
import { getLearnPath } from '../../../../common/src/pages.ts';
import LockedIcon from '../../assets/learn-locked.svg?react';
import { useLearnProgress } from '../../data/use_learn_progress.tsx';
import { OuterPageContent } from '../../util/shared.tsx';
import { Footer } from '../footer.tsx';
import { Header } from '../header.tsx';
import {
  FindRangeLink,
  LearnCard,
  LearnCardList,
  LearnTabBody,
  LearnTabFootnote,
  LearnTabSpacer,
} from './learn_cards.tsx';
import {
  learnClass,
  learnClassFootnote,
  LearnClassId,
  LearnClassInfo,
  LearnClassSingerLine,
  learnModuleTileStatus,
} from './learn_classes.ts';
import { useLearnExit } from './learn_navigation.tsx';
import { shortScreen } from './learn_screen_parts.tsx';

// Stable ids so stories and tests can find the screen and its tiles
export const learnClassScreenId = 'learn-class-screen';
export const learnModuleTileTestId = 'learn-module-tile';

// A class screen (issue #3719): what the hub's class card opens. The class name, one line for the
// singer (their part and range on Pitch), the order we suggest, then one tile per module from
// learn_classes.ts - its level and best score with Continue, "Not started" with Start, or a locked
// "Coming soon" tile for a module not built yet - and the footnote saying any of them can come
// first. The footer tabs stay; the header's back arrow returns to the hub, stepping back to it
// when the hub is the entry before (learn_navigation.tsx). Fits one phone screen without
// scrolling, with the hub's short-screen treatment (iPhone SE).
export const LearnClassPage = ({ classId }: { classId: LearnClassId }) => {
  const { progress } = useLearnProgress();
  const info = learnClass(classId);
  const onBack = useLearnExit(getLearnPath());

  return (
    <OuterPageContent>
      <Header title={info.name} onBack={() => void onBack()} />
      <LearnClassScreen info={info} progress={progress} />
      <Footer />
    </OuterPageContent>
  );
};

const LearnClassScreen = ({ info, progress }: { info: LearnClassInfo; progress: LearnProgress }) => (
  <LearnTabBody id={learnClassScreenId} data-class={info.id}>
    <ClassName>{info.name}</ClassName>
    {info.screen && <SingerLine line={info.screen.singerLine} progress={progress} />}
    {info.screen && <SuggestedOrder>{info.screen.suggestedOrder}</SuggestedOrder>}
    <LearnCardList>
      {info.modules.map((module) => {
        const status = learnModuleTileStatus(progress, module);
        const Icon = status.kind === 'comingSoon' ? LockedIcon : module.icon;
        return (
          <LearnCard
            key={module.name}
            to={status.to}
            next={status.next}
            icon={<Icon />}
            title={module.name}
            description={module.description}
            status={status.text}
            chevron={status.to !== undefined}
            data-testid={learnModuleTileTestId}
            data-status={status.kind}
          />
        );
      })}
    </LearnCardList>
    <LearnTabSpacer />
    <LearnTabFootnote>{learnClassFootnote}</LearnTabFootnote>
  </LearnTabBody>
);

// "Your part: Alto · G3 to D5", or the Range Finder's call to action until a range is saved
const SingerLine = ({ line, progress }: { line: LearnClassSingerLine; progress: LearnProgress }) => {
  if (line.kind === 'text') {
    return <SingerText>{line.text}</SingerText>;
  }
  const { range } = progress;
  if (!range) {
    return (
      <FindRangeRow>
        <FindRangeLink />
      </FindRangeRow>
    );
  }
  const part = progress.voicePart ?? voicePartForRange(range);
  return (
    <SingerText>
      Your part: <Part>{voicePartName(part)}</Part> · {formatVocalRange(range)}
    </SingerText>
  );
};

const ClassName = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 26px;
  line-height: 1.3;

  ${shortScreen} {
    font-size: 22px;
  }
`;

const SingerText = styled.div`
  font-size: 15px;
  line-height: 22px;
  color: var(--color-text-darker);
`;

const Part = styled.span`
  font-family: Jost-SemiBold, Arial, sans-serif;
  color: var(--color-tab-selected);
`;

const FindRangeRow = styled.div`
  display: flex;
  margin-top: 4px;
`;

const SuggestedOrder = styled.div`
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.4;
  color: var(--color-text-darker);

  ${shortScreen} {
    margin-top: 8px;
  }
`;
